<script setup lang="ts">
import { computed, type PropType, useSlots } from "vue";
import type { UIColor } from "../../..";
import Overlay from "../../internal/Overlay/Overlay.vue";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  /*
      Text color
      values: see src/css/color.css
    */
  color: {
    type: String as PropType<UIColor>,
    required: true,
  },

  colorBorder: {
    type: String as PropType<UIColor>,
    required: false,
  },

  size: {
    type: String as PropType<"collapsed" | "small" | "medium" | "large">,
    required: true,
  },

  /*
      Disabled state
    */
  disabled: {
    type: Boolean,
    required: false,
  },

  /*
      Unsets border-radius and any interaction flurishes, like hover effects, etc.
    */
  collapse: {
    type: Boolean,
    required: false,
  },

  loading: {
    type: Boolean,
    required: false,
    default: false,
  },

  /*
      Underlines text, usually used with --color-transparent
    */
  underline: {
    type: Boolean,
    default: false,
  },

  /*
      Should padding be omitted
      values: true, false (default)
    */
  omitPadding: {
    type: Boolean,
    default: false,
  },

  /*
      Should the card have a shadow
      values: small, medium, large
    */
  shadow: {
    type: String as PropType<"small" | "medium" | "large">,
    default: "",
  },
});

const slots = useSlots();

const borderRadius = computed(() => {
  switch (props.size) {
    case "small":
      return "--border-radius-small";
    case "medium":
      return "--border-radius-medium";
    case "large":
      return "--border-radius-large";
  }
});

const iconBeforePopulated = computed(() => slots.iconBefore !== undefined);
const iconPopulated = computed(() => slots.icon !== undefined);
const copyPopulated = computed(() => slots.copy !== undefined);
const iconAfterPopulated = computed(() => slots.iconAfter !== undefined);

const styles = computed(() => {
  let color: string = props.color;

  const matchTransparent = props.color.match(/--color-transparent/);

  if (matchTransparent !== null) {
    color = "rgba(0, 0, 0, 0.05)";
  } else {
    const matchColor = props.color.match(/--color-([a-z]+)-?\d{0,3}/);
    if (matchColor !== null) {
      if (matchColor[1] === "white") {
        color = `var(--color-black-50)`;
      } else if (matchColor[1] === "black") {
        color = `var(--color-white)`;
      } else {
        color = `var(--color-${matchColor[1]}-400)`;
      }
    }
  }

  return {
    "--push-button-color": `var(${props.color})`,
    "--push-button-color-highlight": color,
    "--push-button-border-radius": `var(${borderRadius.value})`,
    "--push-button-color-border": `var(${props.colorBorder})`,
  };
});
</script>

<template>
  <div
    :class="[
      $style.container,
      'push-button',
      loading ? 'push-button-loading-true' : '',
      underline && 'push-button-underline',
      omitPadding && $style.omitPadding,
      colorBorder && $style.border,
      disabled && $style.disabled,
      $style[`shadow-${shadow}`],
    ]"
    :style="styles"
  >
    <div
      :class="[$style.loader, $style[loading ? 'loadingTrue' : 'loadingFalse']]"
    />

    <Overlay
      v-if="disabled"
      :borderRadius="borderRadius"
      colorBackground="--color-primary"
      colorForeground="--color-primary"
      opacityBackground="0.075"
      opacityForeground="0.1"
    />

    <button
      v-bind="$attrs"
      :class="[
        $style.button,
        $style[size],
        $style[color],
        $style[collapse ? 'collapse' : ''],
      ]"
    >
      <span
        v-if="iconBeforePopulated"
        :class="[$style.iconBefore, $style[size]]"
      >
        <slot name="iconBefore" />
      </span>

      <span v-if="iconPopulated" :class="[$style.icon, $style[size]]">
        <slot name="icon" />
      </span>

      <span v-if="copyPopulated" class="push-button-copy">
        <slot name="copy" />
      </span>

      <span v-if="iconAfterPopulated" :class="[$style.iconAfter, $style[size]]">
        <slot name="iconAfter" />
      </span>
    </button>
  </div>
</template>

<style module>
.container {
  display: inline-flex;
  flex-wrap: nowrap;
  position: relative;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  width: fit-content;
}

.container.disabled {
  cursor: not-allowed;
}

.shadow-small {
  box-shadow: var(--box-shadow-small);
}

.shadow-medium {
  box-shadow: var(--box-shadow-medium);
}

.shadow-large {
  box-shadow: var(--box-shadow-large);
}

.loader {
  pointer-events: none;
  display: inline-flex;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  transition-property: opacity;
  transition-duration: var(--transition-duration-shortest);
  transition-timing-function: ease-in-out;
  background-color: var(--push-button-color-highlight);
  mask-position: center center;
  mask-repeat: no-repeat;
  mask-size: contain;
  mask-image: url("./assets/loader.svg");
}

.loader.loadingTrue {
  opacity: 1;
}

.loader.loadingFalse {
  opacity: 0;
}

.button {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  transition-property: background-color, border-color, box-shadow, transform,
    opacity;
  transition-duration: var(--transition-duration-shortest);
  transition-timing-function: ease-in-out;
  background-color: var(--push-button-color);
  -webkit-touch-callout: none;
  /* iOS Safari */
  -webkit-user-select: none;
  /* Safari */
  -ms-user-select: none;
  /* Internet Explorer/Edge */
  user-select: none;
  border-style: none;
  font: inherit;
}

.button:hover,
.button:focus {
  outline: none;
}

.button:hover {
  cursor: pointer;
}

.border {
  border: 1px solid var(--push-button-color-border);
}

.iconBefore,
.icon,
.iconAfter {
  display: flex;
  align-items: center;
  filter: inherit;
}

.button.small {
  padding: calc(1rem / 4) calc(2rem / 4);
}

.button.medium {
  padding: calc(1.5rem / 4) calc(3rem / 4);
}

.button.large {
  padding: calc(2rem / 4) calc(4rem / 4);
}

.container.omitPadding .button {
  padding: 0 !important;
}

.button.small.collapse,
.button.medium.collapse,
.button.large.collapse {
  border-radius: unset;
}

.button.small.collapse:hover,
.button.medium.collapse:hover,
.button.large.collapse:hover {
  box-shadow: unset;
  background-color: unset;
}

.button.--color-transparent {
  box-shadow: none;
}

.iconBefore.collapse {
  transform: translateX(calc(-0.5rem / 4));
}

.iconBefore.small {
  transform: translateX(calc(-0.5rem / 4));
}

.iconBefore.medium {
  transform: translateX(calc(-1rem / 4));
}

.iconBefore.large {
  transform: translateX(calc(-1.5rem / 4));
}

.iconAfter.collapse {
  transform: translateX(calc(0.5rem / 4));
}

.iconAfter.small {
  transform: translateX(calc(0.5rem / 4));
}

.iconAfter.medium {
  transform: translateX(calc(1rem / 4));
}

.iconAfter.large {
  transform: translateX(calc(1.5rem / 4));
}

:global(.push-button.push-button-loading-true button span) {
  opacity: 0;
}

:global(
    .push-button.push-button-loading-true button:not(.--color-transparent):hover
  ) {
  box-shadow: none;
}

:global(.push-button.push-button-underline .push-button-copy) {
  text-decoration: underline;
}

:global(.push-button.push-button-underline:hover .push-button-copy) {
  text-decoration: none;
}
</style>
